'use client';

import { Check, Copy } from 'lucide-react';
import { useEffect, useState } from 'react';
import PrismAsyncLight from 'react-syntax-highlighter/dist/esm/prism-async-light';

import { jetBrainsMono } from '@/shared/config';

import { langLabel } from '../../lib/consts';
import {
	ensureLanguage,
	getResolvedLangId,
} from '../../lib/register-languages';
import { prismDark } from '../theme/prism-dark';
import { prismLight } from '../theme/prism-light';

type CodeProps = {
	className?: string;
	children?: React.ReactNode;
	showToolbar?: boolean;
};

/**
 * Code-блок для react-markdown. В v9+ проп `inline` убрали, поэтому определяем
 * inline vs block по наличию \n в содержимом (fenced-блок всегда многострочный).
 */
export function CodeBlock({
	className,
	children,
	showToolbar = false,
	...rest
}: CodeProps) {
	const raw = String(children ?? '');
	const isBlock = /\n/.test(raw) || /^language-/.test(className ?? '');

	if (!isBlock) {
		return (
			<code
				className="rounded bg-gray-5 px-[5px] py-[2px] font-mono text-[0.875em] ring-1 ring-gray-4 dark:bg-gray-2 dark:text-white dark:ring-gray-3"
				{...rest}
			>
				{children}
			</code>
		);
	}

	const langId =
		(className ?? '').match(/language-(\w+)/)?.[1]?.toLowerCase() ?? null;

	return (
		<HighlightedCode
			code={raw.replace(/\n$/, '')}
			langId={langId}
			showToolbar={showToolbar}
		/>
	);
}

function HighlightedCode({
	code,
	langId,
	showToolbar,
}: {
	code: string;
	langId: string | null;
	showToolbar: boolean;
}) {
	const [copied, setCopied] = useState(false);
	const [ready, setReady] = useState(false);

	useEffect(() => {
		let cancelled = false;
		if (!langId) {
			setReady(true);
			return;
		}
		ensureLanguage(langId).then(() => {
			if (!cancelled) setReady(true);
		});
		return () => {
			cancelled = true;
		};
	}, [langId]);

	const handleCopy = () => {
		navigator.clipboard.writeText(code);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	const resolvedLang = getResolvedLangId(langId);
	const label = langLabel(langId);

	return (
		<div
			className={`${jetBrainsMono.variable} group relative mt-4 overflow-hidden ${showToolbar ? 'rounded-2xl' : 'rounded-md'} border-gray-4 bg-gray-4/30 dark:border-gray-3 dark:bg-gray-1`}
		>
			<div
				className={
					showToolbar
						? 'absolute right-2 top-2 z-10 flex items-center gap-3'
						: 'pointer-events-none absolute right-2 top-2 z-10 flex items-center gap-2 opacity-0 transition-opacity duration-150 group-hover:pointer-events-auto group-hover:opacity-100'
				}
			>
				{label && (
					<span className="select-none font-mono text-xs text-gray-3">
						{label}
					</span>
				)}
				<button
					type="button"
					onClick={handleCopy}
					aria-label="Скопировать код"
					className="flex h-7 w-7 items-center justify-center rounded-md border border-gray-4 bg-white text-gray-3 transition-colors hover:text-gray-1 dark:border-gray-3 dark:bg-gray-2 dark:text-gray-4 dark:hover:text-white"
				>
					{copied ? (
						<Check className="h-3.5 w-3.5" />
					) : (
						<Copy className="h-3.5 w-3.5" />
					)}
				</button>
			</div>
			<div className="block dark:hidden">
				<PrismAsyncLight
					language={ready && resolvedLang ? resolvedLang : 'text'}
					style={prismLight}
					customStyle={{
						margin: 0,
						padding: showToolbar ? '1rem 5.5rem 1rem 1.25rem' : '1rem 1.25rem',
						background: 'transparent',
						fontSize: '14px',
						lineHeight: '1.6',
						overflowX: 'auto',
					}}
				>
					{code}
				</PrismAsyncLight>
			</div>
			<div className="hidden dark:block">
				<PrismAsyncLight
					language={ready && resolvedLang ? resolvedLang : 'text'}
					style={prismDark}
					customStyle={{
						margin: 0,
						padding: showToolbar ? '1rem 5.5rem 1rem 1.25rem' : '1rem 1.25rem',
						background: 'transparent',
						fontSize: '14px',
						lineHeight: '1.6',
						overflowX: 'auto',
					}}
				>
					{code}
				</PrismAsyncLight>
			</div>
		</div>
	);
}
